import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { hasPermission } from "../../../lib/permissions";
import { CONFIGS, Lines } from "./BusinessTabs";
import { AdminTable, ListFrame, SearchBox } from "../ListBits";
import { ErrorCard } from "../AdminBits";
import { BarList, CountTabs, FilterBar, KpiStrip, MobileSort, PaginationBar, PeriodPicker, usePeriod } from "../analysis/AnalysisBits";
import useAdminFetch from "../../../hooks/useAdminFetch";
import useAnalysisList from "../../../hooks/useAnalysisList";
import { exportColumns, exportQuery, facetOf, facetTotal, tabsWithCounts } from "../../../lib/jamoraAnalysis";

// Libellé d'un code : les cartes de libellés sont soit { CODE: "Libellé" }, soit { CODE: ["Libellé", ton] }.
export const labelsOf = (map) => Object.fromEntries(Object.entries(map).map(([code, value]) => [code, Array.isArray(value) ? value[0] : value]));

// Une vue d'un groupe : la liste de BusinessTabs (`CONFIGS[view]`), plus ce qui la rend analysable.
//   period: la liste suit la période choisie · facet: [groupe, champ] des compteurs d'onglets du résumé · statusParam: paramètre filtré par les onglets
//   statuses: libellés des onglets · sorts: colonne -> tri accepté par Jamora · filters: listes déroulantes · search: texte du champ de recherche
function AnalysisList({ schema, view, spec, state, patch, range, summary, size, onSize }) {
  const config = CONFIGS[view];
  const [opened, setOpened] = useState(null);
  const [withReserve, setWithReserve] = useState(false);
  const param = spec.statusParam || "status";
  const filters = { ...(state.status ? { [param]: state.status } : {}), ...Object.fromEntries(Object.entries(state.f).filter(([, v]) => v)) };
  const list = useAnalysisList(`/pharmacies/${schema}/${config.path}`, { range: spec.period ? range : null, filters, search: state.search, ordering: state.ordering, limit: size });
  useEffect(() => {
    if (!withReserve && config.reserveField && list.items.some((row) => row[config.reserveField] !== undefined)) setWithReserve(true);
  }, [list.items, withReserve, config.reserveField]);
  const baseColumns = typeof config.columns === "function" ? config.columns(withReserve) : config.columns;
  const columns = baseColumns.map((c) => (spec.sorts?.[c.id] ? { ...c, sort: spec.sorts[c.id] } : c));
  const tabs = spec.facet ? tabsWithCounts(spec.all || "Tous", labelsOf(spec.statuses), facetOf(summary, ...spec.facet)) : null;
  const { user } = useAuth();
  const canExport = hasPermission(user, "jamora_admin:export");
  const dropdowns = (spec.filters || []).filter((f) => !f.reserve || withReserve);
  return (
    <div className="space-y-3" data-testid={`business-${config.path}`}>
      {tabs && <CountTabs label="État" tabs={tabs} value={state.status || ""} onChange={(v) => patch({ status: v })} testid={`${view}-status`} />}
      {(dropdowns.length > 0 || spec.search) && (
        <FilterBar filters={dropdowns} values={state.f} onChange={(p, value) => patch({ f: { ...state.f, [p]: value } })} onClear={() => patch({ f: {} })} testid={`${view}-filters`}>
          {spec.search && <SearchBox value={state.search} onChange={(v) => patch({ search: v })} placeholder={spec.search} testid={`${view}-search`} />}
        </FilterBar>
      )}
      <MobileSort columns={columns} ordering={state.ordering} onOrder={(o) => patch({ ordering: o })} />
      <ListFrame list={list} empty={{ icon: config.icon, title: config.empty, hint: "Essayez une autre période ou un autre filtre.", testid: `${view}-empty` }}>
        <AdminTable columns={columns} rows={list.items} rowKey={config.rowKey} testid={`${view}-table`} ordering={state.ordering} onOrder={(o) => patch({ ordering: o })}
                    onRowClick={config.lines ? setOpened : undefined} />
      </ListFrame>
      {canExport && list.phase === "ready" && (
        <div className="flex justify-end">
          <ExportButton schema={schema} path={config.path} view={view} columns={columns} rows={list.items} total={list.total}
                        query={{ range: spec.period ? range : null, filters, search: state.search, ordering: state.ordering }} />
        </div>
      )}
      <PaginationBar list={list} size={size} onSize={onSize} noun={spec.noun} testid={`${view}-pager`} />
      {config.lines && <Lines schema={schema} kind={config.lines} row={opened} onClose={() => setOpened(null)} />}
    </div>
  );
}

// « Exporter (CSV) » : une copie de la liste telle qu'affichée (5 000 lignes au plus), journalisée côté serveur avant l'envoi.
function ExportButton({ schema, path, view, query, columns, rows, total }) {
  const [busy, setBusy] = useState(false);
  const chosen = exportColumns(columns, rows[0]);
  const run = async () => {
    setBusy(true);
    try {
      const { data, headers } = await api.get(`/jamora-admin/pharmacies/${schema}/export/${path}.csv?${exportQuery(query, chosen)}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `jamora-${schema}-${path}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(headers["x-export-truncated"] === "true" ? "Export terminé : les 5 000 premières lignes seulement. Affinez la période ou les filtres." : `Export terminé : ${headers["x-export-rows"]} ligne(s).`);
    } catch (e) {
      let message = "Export impossible pour le moment.";
      try { message = JSON.parse(await e.response.data.text()).detail?.message || message; } catch { /* message générique */ }
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button type="button" variant="outline" size="sm" onClick={run} disabled={busy || !chosen.length || !total} data-testid={`${view}-export`}>
      {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Download size={15} className="mr-1.5" />} Exporter (CSV)
    </Button>
  );
}

const blank = () => ({ status: "", ordering: "", search: "", f: {} });

// Un groupe de listes d'une pharmacie : période, bandeau d'indicateurs du résumé, onglets de vues, et la vue choisie.
// `kpis(summary, { view, state, pick })` rend les tuiles ; `extra(summary)` : un bloc facultatif sous le bandeau (répartition par mode de paiement).
export default function AnalysisGroup({ schema, group, testid, views, kpis, extra, first }) {
  const period = usePeriod();
  const [view, setView] = useState(first);
  const [size, setSize] = useState(50);
  const [states, setStates] = useState({});
  const state = { ...blank(), ...(states[view] || {}) };
  const patch = (change) => setStates((all) => ({ ...all, [view]: { ...state, ...change } }));
  const summary = useAdminFetch(`/pharmacies/${schema}/summary/${group}`, { date_from: period.range.from, date_to: period.range.to });
  const pick = (target, change = {}) => () => { setView(target); setStates((all) => ({ ...all, [target]: { ...blank(), ...change } })); };
  const items = summary.data ? kpis(summary.data, { view, state, pick }) : [];
  const spec = views[view];
  const note = (() => {
    const applies = spec.period ? `Période : ${period.label}` : "Cette liste ne dépend pas de la période.";
    return applies;
  })();
  return (
    <div className="space-y-4" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <PeriodPicker period={period} />
        <p className="text-xs text-slate-500" data-testid={`${group}-period-note`}>{note}</p>
      </div>
      {summary.phase === "error" ? <ErrorCard message={summary.error} onRetry={summary.reload} testid="summary-error" /> : <KpiStrip items={items} loading={summary.phase !== "ready"} testid={`${group}-kpis`} />}
      {summary.data && extra?.(summary.data)}
      <CountTabs label="Vue" tabs={Object.entries(views).map(([id, v]) => [id, v.label, v.facet ? facetTotal(facetOf(summary.data, ...v.facet)) : null])} value={view} onChange={setView} testid={`${group}-view`} />
      <AnalysisList key={view} schema={schema} view={view} spec={spec} state={state} patch={patch} range={period.range} summary={summary.data} size={size} onSize={setSize} />
    </div>
  );
}

export { BarList };
